import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { APP_BASE, APP_HEADER, appPath } from "./app";
import { useAuth } from "./auth";
import { useApi } from "./index";
import type { SessionResponse } from "./types";

/**
 * Staff single sign-on through a live directory provider (the Ministry account: Keycloak with its authenticator step, on OCI). The sign-in
 * page starts the flow; the provider sends the browser back to the app's `/login?code=…&state=…` (`/admin/login` for the console under
 * /admin: each app's sign-in page is its own redirect URI), where {@link SsoCallbackGate} completes it
 * before any route guard runs. Simulated providers, account pickers and standing sign-in links are gone (security S2, S7): the only
 * other way in is the password sign-in (with an authenticator code only where a host sets Auth:RequireMfa).
 */
export interface SsoProvider {
  key: string;
  name: string;
  nameAr?: string | null;
  kind: string;
  iconUrl?: string | null;
  /** Reported by servers from before the strict sign-in; such providers are never offered. */
  simulated?: boolean;
}

export interface SsoFailure {
  code: string;
  message: string;
}

interface SsoStart {
  state: string;
  authorizeUrl?: string | null;
}

interface Pending {
  state: string;
  provider: string;
  from?: string | null;
  /** This return answers a second request for the password and MFA (`prompt=login`), so it is not asked for a third time. */
  reauth?: boolean;
}

const PENDING_KEY = "ncp.sso.pending";
const FAILURE_KEY = "ncp.sso.failure";
const CALLBACK_PARAMS = ["code", "state", "session_state", "error", "error_description", "error_uri", "iss", "client_info"];

function readPending(): Pending | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as Pending) : null;
  } catch {
    return null;
  }
}

/** Enabled live staff providers for the sign-in page; empty while loading and when none is configured (the laptop). */
export function useSsoProviders(): { providers: SsoProvider[]; loading: boolean } {
  const api = useApi();
  const [providers, setProviders] = useState<SsoProvider[] | null>(null);
  useEffect(() => {
    let alive = true;
    // Plain fetch: the list is public and a refusal must not reach the client's 401 handler. The page's origin and base path name the
    // application, which offers only its own providers (ECONIC and NCP are registered separately in the Ministry's directory).
    fetch(`${api.baseUrl}/api/auth/providers?origin=${encodeURIComponent(window.location.origin + APP_BASE)}`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? (res.json() as Promise<SsoProvider[]>) : []))
      .then((list) => alive && setProviders((Array.isArray(list) ? list : []).filter((p) => !p.simulated)))
      .catch(() => alive && setProviders([]));
    return () => {
      alive = false;
    };
  }, [api]);
  return { providers: providers ?? [], loading: providers === null };
}

/**
 * Leaves for the provider, which sends the browser back to `/login`. With `reauth` the provider asks for the password and MFA again: the
 * platform accepts a directory's MFA only within the provider's window (Microsoft Entra ID: 12 hours), and an older one is asked for once.
 */
async function startSignIn(api: ReturnType<typeof useApi>, provider: string, from: string | null, reauth: boolean): Promise<void> {
  const res = await api.post<SsoStart>(`/api/auth/oidc/${encodeURIComponent(provider)}/start`, { redirectUri: `${window.location.origin}${appPath("/login")}`, reauthenticate: reauth });
  if (!res.authorizeUrl) throw new Error("provider_unavailable");
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ state: res.state, provider, from, reauth } satisfies Pending));
  window.location.assign(res.authorizeUrl);
}

/** Starts a sign-in with a live provider: the browser leaves for the provider and comes back to `/login`. */
export function useSsoSignIn() {
  const api = useApi();
  const start = useCallback((provider: string, from?: string | null): Promise<void> => startSignIn(api, provider, from ?? null, false), [api]);
  return { start };
}

/** Why the last return from a provider did not sign the user in, shown once by the sign-in page. */
export function useSsoFailure(): [SsoFailure | null, () => void] {
  const [failure, setFailure] = useState<SsoFailure | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem(FAILURE_KEY);
      return raw ? (JSON.parse(raw) as SsoFailure) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    window.sessionStorage.removeItem(FAILURE_KEY);
  }, []);
  return [failure, useCallback(() => setFailure(null), [])];
}

function readCallback(): { state: string; code: string | null; error: string | null; errorDescription: string | null } | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const state = q.get("state");
  if (!state || (!q.get("code") && !q.get("error"))) return null;
  // Only a return this browser started: a stray ?code= on the page is not ours to redeem.
  if (readPending()?.state !== state) return null;
  return { state, code: q.get("code"), error: q.get("error"), errorDescription: q.get("error_description") };
}

function restoreUrl(from: string | null | undefined): void {
  const url = new URL(window.location.href);
  for (const key of CALLBACK_PARAMS) url.searchParams.delete(key);
  if (from) url.searchParams.set("from", from);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  // The data router read the location before this ran; a synthetic popstate makes it pick up the clean URL.
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

/**
 * Completes a return from an identity provider before anything below it renders, so route guards never see the signed-out state in
 * between. A refusal (no mapped role, deactivated account…) is kept for the sign-in page, which the user lands on either way.
 */
export function SsoCallbackGate({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const api = useApi();
  const { setSession } = useAuth();
  const [callback] = useState(readCallback);
  const [pending, setPending] = useState(callback !== null);
  const started = useRef(false);

  useEffect(() => {
    if (!callback || started.current) return;
    started.current = true;
    const pendingSignIn = readPending();
    const from = pendingSignIn?.from;
    window.sessionStorage.removeItem(PENDING_KEY);
    const fail = (failure: SsoFailure) => window.sessionStorage.setItem(FAILURE_KEY, JSON.stringify(failure));
    let leaving = false;
    void (async () => {
      try {
        // Plain fetch: a refused sign-in must not trigger the client's 401 handler.
        const res = await fetch(`${api.baseUrl}/api/auth/oidc/callback`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", [APP_HEADER]: APP_BASE },
          body: JSON.stringify(callback),
        });
        if (res.ok) setSession((await res.json()) as SessionResponse, { arrival: true });
        else {
          const problem = (await res.json().catch(() => null)) as { title?: string; code?: string } | null;
          // The directory's MFA is older than the platform accepts: ask for it again once, straight away.
          if (problem?.code === "mfa_reauthenticate" && pendingSignIn && !pendingSignIn.reauth) {
            try {
              await startSignIn(api, pendingSignIn.provider, from ?? null, true);
              leaving = true;
              return;
            } catch {
              // Falls through to the sign-in page with the reason.
            }
          }
          fail({ code: problem?.code ?? String(res.status), message: problem?.title ?? `${res.status} ${res.statusText}` });
        }
      } catch {
        fail({ code: "network", message: "The sign-in could not be completed. Check the connection and try again." });
      } finally {
        // Leaving for the provider again: the gate keeps its fallback until the browser has gone.
        if (!leaving) {
          restoreUrl(from);
          setPending(false);
        }
      }
    })();
  }, [api, callback, setSession]);

  return <>{pending ? fallback : children}</>;
}
